/**
 * Footer
 * -------
 * Shared legal footer for the Rage Room Scheduler experience. Surfaces
 * Pluto legal links and optional sweepstakes rules using MUI primitives.
 */
import { useCallback, useMemo } from 'react';
import { useTheme, alpha } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Link from '@mui/material/Link';

const baseLegalTextSx = {
  fontSize: 12,
  lineHeight: '21px',
  fontWeight: 400,
};

const baseSeparatorSx = {
  ...baseLegalTextSx,
  px: 0.5,
};

const Footer = () => {
  const theme = useTheme();

  const legalTextSx = useMemo(
    () => ({
      ...baseLegalTextSx,
      color: theme.palette.text.secondary,
    }),
    [theme],
  );

  const linkSx = useMemo(() => {
    const linkColor = theme.palette.secondary.main;

    return {
      ...legalTextSx,
      color: linkColor,
      textDecorationColor: alpha(linkColor, 0.4),
      '&:hover': {
        textDecorationColor: linkColor,
      },
    };
  }, [legalTextSx, theme]);

  const separatorSx = useMemo(
    () => ({
      ...baseSeparatorSx,
      color: theme.palette.text.secondary,
    }),
    [theme],
  );

  const legalLinks = useMemo(() => {
    const links = [
      {
        label: "Terms of Use",
        href: 'https://legal.paramount.com/us/en/plutotv/terms-of-use?r=pluto.tv',
      },
      {
        label: "Privacy Policy",
        href: 'https://privacy.paramount.com/policy',
      },
      {
        label: "California Notice",
        href: 'https://privacy.paramount.com/en/policy#additional-information-us-states',
      },
    ];

    return links;
  }, []);

  const handlePrivacyChoices = useCallback((event) => {
    event.preventDefault();
    cbsoptanon.ot.showPreferenceCenter();
  }, []);

  const linkItems = [
    ...legalLinks.map((link) => ({
      key: link.label,
      node: (
        <Link
          key={link.label}
          href={link.href}
          target="_blank"
          rel="noreferrer"
          underline="hover"
          sx={linkSx}
        >
          {link.label}
        </Link>
      ),
    })),
    {
      key: 'privacy-choices',
      node: (
        <Link
          key="privacy-choices"
          component="button"
          type="button"
          onClick={handlePrivacyChoices}
          underline="hover"
          sx={{
            ...linkSx,
            cursor: 'pointer',
            backgroundColor: 'transparent',
            border: 0,
            p: 0,
            m: 0,
          }}
        >
          Your Privacy Choices
        </Link>
      ),
    },
  ];

  return (
    <Box
      component="footer"
      sx={{
        width: '100%',
        mt: { xs: 6, md: 8 },
        mb: { xs: 6, md: 8 },
      }}
    >
      <Stack spacing={1.5} alignItems="center" textAlign="center" sx={{ maxWidth: 480, mx: 'auto' }}>
        <Typography sx={legalTextSx}>© 2025 Pluto Inc. All rights reserved.</Typography>
        <Stack
          direction="row"
          spacing={0.5}
          flexWrap="wrap"
          justifyContent="center"
          alignItems="center"
        >
          {linkItems.map((item, index) => (
            <Stack
              key={item.key}
              direction="row"
              alignItems="center"
              sx={{ my: 0.25 }}
              spacing={0}
            >
              {item.node}
              {index < linkItems.length - 1 && (
                <Typography component="span" sx={separatorSx}>
                  |
                </Typography>
              )}
            </Stack>
          ))}
        </Stack>
      </Stack>
    </Box>
  );
};

export default Footer;